// Nexus Support Launcher — floating "Create a support ticket" widget.
// See how-to-add-nexus.md. Base URL comes from REACT_APP_NEXUS_BASE_URL
// (per-env, e.g. https://nexus-stg.amili.asia on staging) — left unset in
// prod .env files until Nexus itself ships to production.
//
// window.nexus must be set before nexus.js loads, and nexus.js only reads
// it once (at mount, to build the button/icon) — so we set it synchronously
// right here, before appending the script tag, rather than from a React
// effect (which could run after nexus.js has already mounted with wrong
// defaults). Reactive fields (reporter_name/reporter_email) are layered on
// afterwards by mutating window.nexus.context directly — nexus.js reads
// context fresh on every click, so that's safe from anywhere, anytime.
export interface NexusConfig {
  source: string;
  ticketDraftApiUrl: string;
  ticketCreateUrl: string;
  iconUrl: string;
  context?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

declare global {
  interface Window {
    nexus?: NexusConfig;
    NexusLauncher?: {
      show: () => void;
      hide: () => void;
      update: (config: NexusConfig) => void;
    };
  }
}

const nexusBaseUrl = process.env.REACT_APP_NEXUS_BASE_URL;

// Guards against double-injection (dev HMR re-running this module, or any
// other re-entry into this file within the same document) — without this,
// a second run appends a second <script>, and nexus.js mounts a second
// launcher/button/listener set that the first is never torn down for.
const alreadyLoaded =
  !!window.NexusLauncher ||
  !!document.querySelector(`script[src="${nexusBaseUrl}/nexus.js"]`);

if (nexusBaseUrl && !alreadyLoaded) {
  window.nexus = {
    source: 'LILAC',
    ticketDraftApiUrl: `${nexusBaseUrl}/api/public/ticket-drafts`,
    ticketCreateUrl: `${nexusBaseUrl}/tickets/create`,
    iconUrl: `${nexusBaseUrl}/nexus_support-Icon.png`,
    metadata: { environment: process.env.REACT_APP_NODE_ENV },
  };

  const script = document.createElement('script');
  script.src = `${nexusBaseUrl}/nexus.js`;
  script.defer = true;
  // authContext.tsx calls NexusLauncher.hide()/show() based on login state
  // and tenant org type, but that global doesn't exist until this script has
  // loaded and mounted the button — so signal readiness rather than making
  // callers guess or poll.
  script.onload = () => window.dispatchEvent(new Event('nexus:ready'));
  document.head.appendChild(script);
}
